import "./served-hosts.schemas.js";
import { RESOURCE_SLUG_PATTERN } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/mail-limit-sync.schemas.ts
/**
* The CP to DP push of one tenant's daily app-mail limit (plan `2026-10-09-app-mail`, D-24, D-27, R-006).
*
* A platform administrator sets the limit on the CP org record (`platform.organizations`, WS-8). platform-api
* then pushes it to each data plane the org is placed on, over the relay that branding uses. The DP receiver
* (core-api `POST /internal/mail-limit-sync`, WS-5) writes it to `auth.organizations.app_mail_per_day` (WS-6),
* and the app-mail admission counts the app mail of all apps of the tenant against it (D-27).
*
* Readers and writers, each of which imports this module and keeps no copy:
* - platform-api: the `PUT /v1/orgs/:orgId/mail-limit` body reads its value rule from
*   `MailLimitSyncRequestSchema.shape.appMailPerDay`, and the sync client builds the push body (WS-8);
* - the relay in provisioning-svc binds the body unchanged;
* - core-api: the receiver binds the body through `@Body({ schema })` (WS-5);
* - cp-admin-ui: the Tenants page field reads {@link APP_MAIL_PER_DAY_MIN} and {@link APP_MAIL_PER_DAY_MAX} (WS-9).
*
* The file takes the shape of `branding-sync.schemas.ts`. The type is `z.infer<>` of the schema, so the wire
* and the TypeScript cannot drift. The object is strict, so an unknown key is refused at every hop. No
* `.transform()` appears here, so the schema stays representable for `createOpenApiDocument()`.
*
* This module does no I/O and holds no state.
*/
/**
* The DP route the CP pushes one org's app-mail limit to: core-api `POST /internal/mail-limit-sync`.
*
* The route copies `BRANDING_SYNC_ROUTE` (index defaults row "A new module's shape"):
* - it is reached only over the CP to DP mTLS listener, behind `CpMtlsGuard` (XFCC CN `cp-platform`) and
*   `@Public()`;
* - it binds {@link MailLimitSyncRequestSchema} and answers 400 for a body the schema refuses;
* - it also answers 400 for a `revision` in a year from 0000 to 0099, and for a `revision` more than 300 s
*   past the DP clock, as `ShellSyncService` does (R-026). The DP writes nothing on either answer;
* - it answers 200 with one of three answers, and with no other body:
*   - `{ synced: true }`: a DP org row has the body's `orgSlug`, and the body's `revision` is later than the
*     revision the DP stored for that row, or equal to it. A later revision writes the limit and the revision;
*     an equal revision is a repeat and writes nothing;
*   - `{ synced: false, reason: 'stale' }`: the body's `revision` is earlier than the stored one. The DP
*     writes nothing;
*   - `{ synced: false }`: no row has the slug (an org the DP has not bootstrapped yet). The DP writes nothing.
*
* So of two CP writes, the later revision is the DP's end state, whatever order the pushes arrive in. The CP
* pushes every stored limit again at boot and every 30 minutes (platform-api `OrgMailLimitReconciler`), so a
* failed push, a `{ synced: false }` answer and a DP that bootstraps the org later all converge on the CP row.
*/
const MAIL_LIMIT_SYNC_ROUTE = "/internal/mail-limit-sync";
/**
* The lowest tenant limit, in app mails a day (R-006). A tenant's app mail stops through the org owner's
* kernel consent, never through a limit of 0. 1 is the lowest `orgPerDay` value that `mailLimitsSchema` of
* `@zion/sdk-node` admits.
*/
const APP_MAIL_PER_DAY_MIN = 1;
/**
* The highest tenant limit, in app mails a day (R-006). It equals `MAIL_LIMIT_CEILINGS.orgPerDay` of
* `@zion/sdk-node` and the bound of the CHECK `organizations_app_mail_per_day_check` on
* `auth.organizations`. This package cannot import `@zion/sdk-node` (that package depends on this one), so
* the value is a literal here, and the test of this module pins it.
*/
const APP_MAIL_PER_DAY_MAX = 5e3;
/**
* The body of DP `POST /internal/mail-limit-sync` ({@link MAIL_LIMIT_SYNC_ROUTE}): the CP pushes one org's
* daily app-mail limit. The body is keyed on the org slug alone, because the CP and a DP hold different
* UUIDs for the same tenant and the slug is the one shared key.
*
* Inputs and the outcome of a parse:
* - `orgSlug`: required. A string of {@link SERVED_HOST_ORG_SLUG_MIN_LENGTH} to
*   {@link SERVED_HOST_ORG_SLUG_MAX_LENGTH} characters (2 to 63, the bound of `BrandingSyncRequestSchema`) that
*   matches `RESOURCE_SLUG_PATTERN` (the grammar of `POST /v1/orgs`). The length check runs before the
*   pattern. Any other value is refused on the path `['orgSlug']`.
* - `appMailPerDay`: required, and `null` is a value. `null` clears the tenant limit, so the DP's platform
*   default `MAIL_LIMIT_ORG_PER_DAY` applies. An integer from {@link APP_MAIL_PER_DAY_MIN} to
*   {@link APP_MAIL_PER_DAY_MAX} sets the tenant limit. An absent key, 0, a negative number, 5001 or more, a
*   fraction, `NaN`, an infinity, a numeric string and every other type are refused on the path
*   `['appMailPerDay']` (R-006).
* - `revision`: required. The CP row's revision as `Date.prototype.toISOString()` writes it,
*   `YYYY-MM-DDTHH:mm:ss.sssZ`, 24 characters (`z.iso.datetime({ precision: 3 })`). The fixed width makes
*   string order equal time order, so the DP compares two revisions as strings. A null stored revision is
*   earlier than every revision. An absent key, a revision with an offset other than `Z`, a precision other
*   than milliseconds and a type other than a string are refused on the path `['revision']`. The schema puts
*   no bound on the instant.
* - Any other key, `orgId` among them, is refused with an `unrecognized_keys` issue (defaults row
*   "Identity": the DP derives nothing from a CP org id). A value that is not an object is refused.
*
* A successful parse returns a new object with the three keys and the input values unchanged.
*
* Under the global `ZionStrictValidationPipe` every refusal is
* `400 { statusCode: 400, message: string[], error: 'Bad Request' }`. The schema is pure, never throws from
* `safeParse`, and has no `.transform()`.
*/
const MailLimitSyncRequestSchema = z.object({
	orgSlug: z.string().min(2).max(63).regex(RESOURCE_SLUG_PATTERN),
	appMailPerDay: z.number().int().min(1).max(APP_MAIL_PER_DAY_MAX).nullable(),
	revision: z.iso.datetime({ precision: 3 })
}).strict();
//#endregion
export { APP_MAIL_PER_DAY_MAX, APP_MAIL_PER_DAY_MIN, MAIL_LIMIT_SYNC_ROUTE, MailLimitSyncRequestSchema };

//# sourceMappingURL=mail-limit-sync.schemas.js.map